import ValidationError from "../../../services/exceptions/validation-error";
import ValidationType from "../../../services/exceptions/validation-type";
import FieldAreaType from "../enums/field-area-type";
import { areEqual, deepCopy } from "../../../services/common/object-util";

const error = (message) => new ValidationError(message, ValidationType.Error);

/**
 * Validate the field-marker portions of a game configuration's `general`
 * block. Throws a ValidationError describing the first problem found. Configs
 * that do not use markers (e.g. traditional football) pass through untouched.
 *
 * @param {object} general gameConfiguration.general
 */
export const validateFieldConfig = (general) => {
    if (!general) {
        return;
    }
    const {
        fieldLength,
        firstDownYards,
        firstDownYardSet,
        customFirstDownYardSet,
        fieldAreaSet,
        redZoneLength,
        otTimeControl,
        otMinutesByPeriod
    } = general;

    // PSTATS-1268: OT Period Length is read only while OT Time Control is on,
    // and the settings form hides it while that is off (a hidden field can be
    // saved blank), so it is checked only while it is on. Absent is allowed:
    // overtime then runs at the regulation length, as it did before the
    // setting existed.
    if (
        otTimeControl &&
        otMinutesByPeriod !== undefined &&
        !(Number.isFinite(otMinutesByPeriod) && otMinutesByPeriod >= 1)
    ) {
        throw error(
            "OT Period Length must be at least 1 minute while OT Time Control is on."
        );
    }

    // Yard-to-gain chain distance (used when firstDownFlagStyle <= 0).
    if (
        typeof firstDownYards === "number" &&
        (firstDownYards < 1 || firstDownYards > fieldLength)
    ) {
        throw error(
            `First down yards ${firstDownYards} must be between 1 and ${fieldLength}.`
        );
    }

    // Only validate the spot list when the crew has authored it. Otherwise it
    // is auto-recomputed uniformly at game start (setStartSettings), so its
    // stored value can legitimately be stale for the current field length.
    if (customFirstDownYardSet && Array.isArray(firstDownYardSet)) {
        let previous = -1;
        firstDownYardSet.forEach((yard) => {
            if (typeof yard !== "number" || yard < 0 || yard > fieldLength) {
                throw error(
                    `First down spot ${yard} must be between 0 and ${fieldLength}.`
                );
            }
            if (yard <= previous) {
                throw error("First down spots must be sorted and unique.");
            }
            previous = yard;
        });
    }

    if (Array.isArray(fieldAreaSet)) {
        const sorted = [...fieldAreaSet].sort((a, b) => a.start - b.start);
        let previousEnd = 0;
        sorted.forEach(({ start, end }) => {
            if (
                typeof start !== "number" ||
                typeof end !== "number" ||
                start < 0 ||
                end > fieldLength ||
                start >= end
            ) {
                throw error(
                    `Field area (${start}-${end}) must fall within 0 and ${fieldLength}.`
                );
            }
            if (start < previousEnd) {
                throw error("Field areas cannot overlap.");
            }
            previousEnd = end;
        });
    }

    if (
        typeof redZoneLength === "number" &&
        (redZoneLength < 0 || redZoneLength > fieldLength)
    ) {
        throw error(
            `Red zone length ${redZoneLength} must be between 0 and ${fieldLength}.`
        );
    }
};

/**
 * Layers a saved configuration over a set of defaults, section by section
 * (PSTATS-1380).
 *
 * The settings form saves the WHOLE configuration, so a stored config is a
 * complete snapshot of the settings as they existed the day it was saved. A
 * setting added to the schema afterwards is simply absent from it, and a
 * top-level spread alone would let that stale section replace the defaults
 * outright - so no season or event that already existed would ever receive it.
 * No ruleset switch is needed for that; the switch just makes it obvious.
 *
 * Merging every section rather than a named few is the point: PSTATS-1147
 * covered `general` and `clock` because they were what its own feature needed,
 * and `spots` and `scoring` kept the old behaviour by omission. Naming sections
 * again would leave the next one out the same way.
 *
 * Only sections the DEFAULTS declare are merged, which is how a sport whose
 * ruleset has no clock section - tennis - still never gains one.
 */
export const mergeConfigSections = (defaults, saved) => {
    if (!saved) {
        return defaults;
    }

    const merged = { ...defaults, ...saved };

    Object.keys(defaults).forEach((section) => {
        const base = defaults[section];
        // Sections only; a scalar or an array (_type, a marker set) is carried
        // by the spread above and must not be merged key by key.
        if (base && typeof base === "object" && !Array.isArray(base)) {
            merged[section] = { ...base, ...saved[section] };
        }
    });

    return merged;
};

/**
 * The settings a crew deliberately changed (PSTATS-1380): every value in
 * `saved` that differs from the ruleset defaults it was authored against.
 *
 * Keys are taken from the DEFAULTS, not from the saved config, which is what
 * makes a rule set switch drop leftovers rather than promote them: a key the
 * authoring ruleset never declared is somebody else's setting that an earlier
 * switch left behind, not an edit this crew made.
 *
 * Values compare with `areEqual`, so a marker set or a field-area list counts
 * as one setting and is only an edit when its CONTENTS differ.
 */
export const diffConfigEdits = (defaults, saved) => {
    const edits = {};
    if (!defaults || !saved) {
        return edits;
    }

    Object.keys(defaults).forEach((section) => {
        const base = defaults[section];
        const savedSection = saved[section];
        if (
            !base ||
            typeof base !== "object" ||
            Array.isArray(base) ||
            !savedSection ||
            typeof savedSection !== "object"
        ) {
            return;
        }

        Object.keys(base).forEach((key) => {
            if (key === "_type" || !(key in savedSection)) {
                return;
            }
            if (!areEqual(base[key], savedSection[key])) {
                edits[section] = edits[section] || {};
                edits[section][key] = deepCopy(savedSection[key]);
            }
        });
    });

    return edits;
};

/**
 * Re-express edits against another set of defaults (PSTATS-1380), dropping any
 * setting those defaults do not declare - a rule set cannot be handed a
 * setting it has no rules for.
 *
 * Returns a new configuration; `defaults` is not mutated.
 */
export const applyConfigEdits = (defaults, edits) => {
    const result = deepCopy(defaults);

    Object.keys(edits || {}).forEach((section) => {
        const target = result[section];
        if (!target || typeof target !== "object" || Array.isArray(target)) {
            return;
        }
        Object.keys(edits[section]).forEach((key) => {
            // `in`, not truthiness - 0 and false are real settings.
            if (key in target) {
                target[key] = deepCopy(edits[section][key]);
            }
        });
    });

    return result;
};

/**
 * PSTATS-1268: until OT Period Length existed, a timed overtime period ran
 * for the regulation Period Length. A config saved with OT Time Control on
 * and no OT length keeps that length, rather than taking the default, until
 * a crew changes it.
 *
 * @param {object} gameConfiguration
 * @returns {object}
 */
const keepTimedOvertimeLength = (gameConfiguration) => {
    const general = gameConfiguration?.general;
    if (
        !general?.otTimeControl ||
        general.otMinutesByPeriod !== undefined ||
        general.minutesByPeriod === undefined
    ) {
        return gameConfiguration;
    }
    return {
        ...gameConfiguration,
        general: { ...general, otMinutesByPeriod: general.minutesByPeriod }
    };
};

/**
 * PSTATS-1147: clock settings moved from general (clockType) into their own
 * clock section. Lift legacy values off configs saved before the move so
 * readers and the settings form only ever see the clock section. Also keeps
 * a timed overtime's length (keepTimedOvertimeLength). Returns a new
 * gameConfiguration; the input is not mutated. No-op for configs that
 * already have a clock section (or none of the legacy keys).
 *
 * @param {object} gameConfiguration
 * @returns {object}
 */
export const migrateClockConfig = (gameConfiguration) => {
    let result = gameConfiguration;
    const general = gameConfiguration?.general;
    if (!gameConfiguration?.clock && general?.clockType !== undefined) {
        const { clockType, runningClock, ...restOfGeneral } = general;
        result = {
            ...gameConfiguration,
            general: restOfGeneral,
            clock: {
                clockType,
                ...(runningClock !== undefined ? { runningClock } : {})
            }
        };
    }
    return keepTimedOvertimeLength(result);
};

/**
 * Keep redZoneLength consistent with any configured red-zone field area so the
 * existing red-zone stat path (isInRedZone / getRedzoneStatistics) reflects the
 * crew's configuration. The low-end red zone (anchored at the goal line) drives
 * the symmetric redZoneLength; falls back to the high-end zone if only that one
 * is present. Returns a new gameConfiguration; the input is not mutated.
 *
 * @param {object} gameConfiguration
 * @returns {object}
 */
export const normalizeFieldConfig = (gameConfiguration) => {
    const general = gameConfiguration?.general;
    if (!general || !Array.isArray(general.fieldAreaSet)) {
        return gameConfiguration;
    }

    const { fieldLength } = general;

    // Clamp field areas to the field: default areas are authored for the 80yd
    // field, so a smaller fieldLength (preset/override) would otherwise leave
    // out-of-range areas that fail validation. Drop areas entirely past the
    // goal line; clamp the trailing end onto it.
    const fieldAreaSet = general.fieldAreaSet
        .filter((area) => area.start < fieldLength)
        .map((area) => ({ ...area, end: Math.min(area.end, fieldLength) }));

    const redZones = fieldAreaSet.filter(
        (area) => area.type === FieldAreaType.RedZone.code
    );

    const lowEnd = redZones.find((area) => area.start === 0);
    const redZoneLength = redZones.length
        ? lowEnd
            ? lowEnd.end
            : fieldLength - redZones[0].start
        : general.redZoneLength;

    return {
        ...gameConfiguration,
        general: {
            ...general,
            fieldAreaSet,
            redZoneLength
        }
    };
};
